import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AgentTeam,
  type Automation,
  api,
  type ConversationDetail,
  type ConversationListItem,
  type DatasetGroup,
  type Project,
} from '../api';

/** Every conversation. Only for pages that really need all of them (Files). */
export function useConversations() {
  return useQuery({
    queryKey: ['conversations', 'all'],
    queryFn: () => api.listConversations().then((r) => r.data),
    staleTime: 10_000,
  });
}

/** Whether the gateway has a usable speech-to-text model; the dictation button needs it. */
export function useTranscriptionAvailable(): boolean {
  const { data } = useQuery({
    queryKey: ['transcription', 'status'],
    queryFn: () => api.transcriptionStatus(),
    staleTime: 60_000,
    retry: false,
  });
  return data?.available === true;
}

/** Whether the gateway can read text aloud; only asked where answers may be read aloud. */
export function useSpeechAvailable(enabled: boolean): boolean {
  const { data } = useQuery({
    queryKey: ['speech', 'status'],
    queryFn: () => api.speechStatus(),
    staleTime: 60_000,
    retry: false,
    enabled,
  });
  return enabled && data?.available === true;
}

const RECENT_CONVERSATIONS = 100;
const RECENT_PER_PROJECT = 3;

/** The sidebar: the latest conversations plus the latest few of every project. */
export function useRecentConversations() {
  return useQuery({
    queryKey: ['conversations', 'recent'],
    queryFn: () =>
      api
        .listConversations({ limit: RECENT_CONVERSATIONS, perProject: RECENT_PER_PROJECT })
        .then((r) => r.data),
    staleTime: 10_000,
  });
}

/** Number of conversations per project id. */
export function useProjectConversationCounts() {
  return useQuery({
    queryKey: ['conversations', 'counts'],
    queryFn: () => api.projectConversationCounts().then((r) => r.data),
    staleTime: 10_000,
  });
}

const SEARCH_PAGE = 30;

/** Server-side search over the whole history, one page at a time. */
export function useConversationSearch(q: string, projectId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['conversations', 'search', q, projectId],
    enabled,
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.searchConversations({
        q,
        projectId,
        limit: SEARCH_PAGE,
        offset: pageParam,
      }),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
    staleTime: 10_000,
  });
}

export function useBranchConversation(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.branchConversation(id, messageId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useConversation(id: string | null) {
  return useQuery<ConversationDetail | null>({
    queryKey: ['conversation', id],
    queryFn: async () => (id ? api.getConversation(id) : null),
    enabled: !!id,
    staleTime: 0,
    // An answer that is still being made on the server (after a reload) shows up when it is saved.
    refetchInterval: (query) => (query.state.data?.generating ? 3_000 : false),
  });
}

export function useConversationTools(id: string | null) {
  return useQuery({
    queryKey: ['conversation-tools', id],
    queryFn: () => (id ? api.getConversationTools(id).then((r) => r.data) : null),
    enabled: !!id,
    staleTime: 5_000,
  });
}

export function useModels(enabled = true) {
  return useQuery({
    queryKey: ['models'],
    queryFn: () => api.listModels().then((r) => r.data),
    staleTime: 30_000,
    enabled,
  });
}

export function useDatasetGroups(enabled = true) {
  return useQuery({
    queryKey: ['dataset-groups'],
    queryFn: () => api.listDatasetGroups().then((r) => r.data),
    staleTime: 60_000,
    enabled,
  });
}

/** The access groups the user is in: what skills and projects can be shared with. */
export function useSkillSharingGroups(enabled = true) {
  return useQuery({
    queryKey: ['skill-sharing-groups'],
    queryFn: () => api.skillSharingGroups().then((r) => r.data),
    staleTime: 60_000,
    enabled,
  });
}

export function useSkills(enabled = true) {
  return useQuery({
    queryKey: ['skills'],
    queryFn: () => api.listSkills().then((r) => r.data),
    staleTime: 60_000,
    enabled,
  });
}

/** Own turns per skill key this month; unknown (undefined) while loading or when the request fails. */
export function useSkillUsage(enabled = true) {
  return useQuery({
    queryKey: ['skill-usage'],
    queryFn: () => api.skillUsage().then((r) => r.data),
    staleTime: 60_000,
    retry: false,
    enabled,
  });
}

export function useUsage() {
  return useQuery({
    queryKey: ['usage'],
    queryFn: () => api.usage(),
    staleTime: 30_000,
  });
}

/** Usage per model this month; loaded when the account menu opens. */
export function useUsageBreakdown(enabled: boolean) {
  return useQuery({
    queryKey: ['usage', 'breakdown'],
    queryFn: () => api.usageBreakdown(),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useMicrosoftMailStatus() {
  return useQuery({
    queryKey: ['microsoft-mail-status'],
    queryFn: () => api.microsoftMailStatus(),
    staleTime: 30_000,
  });
}

export function useMicrosoftConnectionStatus(enabled = true) {
  return useQuery({
    queryKey: ['microsoft-connection-status'],
    queryFn: () => api.microsoftConnectionStatus(),
    staleTime: 30_000,
    enabled,
  });
}

export function useConnectMicrosoftMail() {
  return useMutation({
    mutationFn: () => api.connectMicrosoftMail(),
    onSuccess: ({ authorizationUrl }) => {
      window.location.href = authorizationUrl;
    },
  });
}

export function useConnectMicrosoft() {
  return useMutation({
    mutationFn: () => api.connectMicrosoft(),
    onSuccess: ({ authorizationUrl }) => {
      window.location.href = authorizationUrl;
    },
  });
}

/** Removes the user's Microsoft 365 connection; the status refreshes afterwards. */
export function useDisconnectMicrosoft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.disconnectMicrosoft(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['microsoft-connection-status'] });
      qc.invalidateQueries({ queryKey: ['microsoft-mail-status'] });
    },
  });
}

export function useAzureDevOpsConnection(enabled = true) {
  return useQuery({
    queryKey: ['azure-devops-connection'],
    queryFn: () => api.azureDevOpsConnection(),
    staleTime: 30_000,
    retry: false,
    enabled,
  });
}

/** Saves or removes the user's own Azure DevOps token; the gateway checks it first. */
export function useAzureDevOpsToken() {
  const qc = useQueryClient();
  const onSuccess = (data: Awaited<ReturnType<typeof api.azureDevOpsConnection>>) =>
    qc.setQueryData(['azure-devops-connection'], data);
  return {
    save: useMutation({
      mutationFn: (token: string) => api.saveAzureDevOpsToken(token),
      onSuccess,
    }),
    remove: useMutation({ mutationFn: () => api.removeAzureDevOpsToken(), onSuccess }),
  };
}

export function useLeexiConnection(enabled = true) {
  return useQuery({
    queryKey: ['leexi-connection'],
    queryFn: () => api.leexiConnection(),
    staleTime: 30_000,
    retry: false,
    enabled,
  });
}

/** Saves or removes the user's own Leexi API key; the gateway checks it first. */
export function useLeexiKey() {
  const qc = useQueryClient();
  const onSuccess = (data: Awaited<ReturnType<typeof api.leexiConnection>>) =>
    qc.setQueryData(['leexi-connection'], data);
  return {
    save: useMutation({
      mutationFn: (key: { keyId: string; keySecret: string }) => api.saveLeexiKey(key),
      onSuccess,
    }),
    remove: useMutation({ mutationFn: () => api.removeLeexiKey(), onSuccess }),
  };
}

export function useToolFailures() {
  return useQuery({
    queryKey: ['admin', 'tool-failures'],
    queryFn: () => api.listToolFailures().then((r) => r.data),
    staleTime: 10_000,
  });
}

export function useProjectPolicy() {
  return useQuery({
    queryKey: ['project-policy'],
    queryFn: () => api.projectPolicy(),
    staleTime: 60_000,
  });
}

/** Admin's chat attachment limits (bytes; null = no limit), from the signed-in user's /me. */
export function useAttachmentLimits() {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });
  return me.data?.attachmentLimits;
}

export function useCreateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      modelId: string;
      title?: string;
      projectId?: string | null;
      systemPrompt?: string;
      selectedSkillSlug?: string | null;
      selectedSkillSlugs?: string[];
      enabledDatasetGroups?: string[];
    }) => {
      const enabledDatasetGroups = await normalizeEnabledDatasetGroups(
        qc,
        input.enabledDatasetGroups,
      );
      return api.createConversation({
        ...input,
        ...(enabledDatasetGroups !== undefined ? { enabledDatasetGroups } : {}),
      });
    },
    onSuccess: (created) => {
      qc.setQueryData(['conversation', created.id], created);
      const addToList = (prev: ConversationListItem[] | undefined) => {
        const item: ConversationListItem = {
          id: created.id,
          title: created.title,
          modelId: created.modelId,
          projectId: created.projectId,
          selectedSkillSlug: created.selectedSkillSlug,
          selectedSkillSlugs: created.selectedSkillSlugs,
          enabledDatasetGroups: created.enabledDatasetGroups,
          updatedAt: created.updatedAt,
          messageCount: 0,
        };
        return prev ? [item, ...prev] : [item];
      };
      qc.setQueryData<ConversationListItem[]>(['conversations', 'all'], addToList);
      qc.setQueryData<ConversationListItem[]>(['conversations', 'recent'], addToList);
      void qc.invalidateQueries({ queryKey: ['conversations', 'counts'] });
    },
  });
}

async function normalizeEnabledDatasetGroups(
  qc: ReturnType<typeof useQueryClient>,
  groups: string[] | undefined,
): Promise<string[] | undefined> {
  if (groups === undefined || groups.length === 0) return groups;
  const availableGroups = await qc.ensureQueryData<DatasetGroup[]>({
    queryKey: ['dataset-groups'],
    queryFn: () => api.listDatasetGroups().then((r) => r.data),
    staleTime: 60_000,
  });
  const available = new Set(availableGroups.map((group) => group.slug));
  return groups.filter((group) => available.has(group));
}

export function usePatchConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: {
        title?: string;
        modelId?: string;
        projectId?: string | null;
        selectedSkillSlug?: string | null;
        selectedSkillSlugs?: string[];
        enabledDatasetGroups?: string[];
        systemPrompt?: string | null;
      };
    }) => {
      const current = await qc.ensureQueryData<ConversationDetail>({
        queryKey: ['conversation', id],
        queryFn: () => api.getConversation(id),
      });
      return api.patchConversation(id, { ...patch, expectedVersion: current.version });
    },
    onSuccess: async (_void, { id }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['conversations'] }),
        qc.invalidateQueries({ queryKey: ['conversation', id] }),
        qc.invalidateQueries({ queryKey: ['conversation-tools', id] }),
      ]);
    },
  });
}

export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects().then((r) => r.data),
    staleTime: 30_000,
  });
}

export function useProject(id: string | null) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: () => (id ? api.getProject(id) : null),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      description?: string | null;
      instructions?: string | null;
      defaultDatasetGroups?: string[];
    }) => api.createProject(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function usePatchProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<{
        name: string;
        description: string | null;
        instructions: string | null;
        defaultDatasetGroups: string[];
        sharedWithEveryone: boolean;
        sharedGroupIds: string[];
        skillSlugs: string[];
      }>;
    }) => api.patchProject(id, patch),
    onSuccess: (project: Project) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project', project.id] });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteProject(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUploadProjectDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, file }: { projectId: string; file: File }) =>
      api.uploadProjectDocument(projectId, file),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['project', doc.projectId] });
      qc.invalidateQueries({ queryKey: ['conversation-tools'] });
    },
  });
}

export function useDeleteProjectDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, documentId }: { projectId: string; documentId: string }) =>
      api.deleteProjectDocument(projectId, documentId),
    onSuccess: (_void, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

export function useReindexProjectDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, documentId }: { projectId: string; documentId: string }) =>
      api.reindexProjectDocument(projectId, documentId),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['project', doc.projectId] });
    },
  });
}

export function usePatchProjectDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      documentId,
      patch,
    }: {
      projectId: string;
      documentId: string;
      patch: Partial<{
        displayName: string | null;
        description: string | null;
        tags: string[];
        active: boolean;
      }>;
    }) => api.patchProjectDocument(projectId, documentId, patch),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['project', doc.projectId] });
      qc.invalidateQueries({ queryKey: ['project-document-preview', doc.projectId, doc.id] });
    },
  });
}

export function useProjectDocumentPreview(projectId: string | null, documentId: string | null) {
  return useQuery({
    queryKey: ['project-document-preview', projectId, documentId],
    queryFn: () =>
      projectId && documentId ? api.getProjectDocumentPreview(projectId, documentId) : null,
    enabled: !!projectId && !!documentId,
    staleTime: 10_000,
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteConversation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useAutomations(agentMode = false, enabled = true) {
  return useQuery({
    queryKey: [agentMode ? 'agents' : 'automations'],
    queryFn: () => api.listAutomations(agentMode),
    staleTime: 10_000,
    refetchInterval: 30_000,
    enabled,
  });
}

export function useCreateAutomation(agentMode = false) {
  const qc = useQueryClient();
  const queryKey = agentMode ? 'agents' : 'automations';
  return useMutation({
    mutationFn: (input: {
      title: string;
      skillSlug: string;
      prompt: string;
      modelId: string;
      enabledDatasetGroups: string[];
      everyMinutes: number;
    }) => api.createAutomation(input, agentMode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
    },
  });
}

export function usePatchAutomation(agentMode = false) {
  const qc = useQueryClient();
  const queryKey = agentMode ? 'agents' : 'automations';
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Automation> }) =>
      api.patchAutomation(id, patch, agentMode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
    },
  });
}

export function useDeleteAutomation(agentMode = false) {
  const qc = useQueryClient();
  const queryKey = agentMode ? 'agents' : 'automations';
  return useMutation({
    mutationFn: (id: string) => api.deleteAutomation(id, agentMode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
    },
  });
}

export function useRunAutomation(agentMode = false) {
  const qc = useQueryClient();
  const queryKey = agentMode ? 'agents' : 'automations';
  return useMutation({
    mutationFn: (id: string) => api.runAutomation(id, agentMode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useMarkAutomationRunSeen(agentMode = false) {
  const qc = useQueryClient();
  const queryKey = agentMode ? 'agents' : 'automations';
  return useMutation({
    mutationFn: (id: string) => api.markAutomationRunSeen(id, agentMode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
    },
  });
}

export function useAgentTeams(enabled = true) {
  return useQuery({
    queryKey: ['agent-teams'],
    queryFn: () => api.listAgentTeams(),
    enabled,
    staleTime: 10_000,
    refetchInterval: enabled ? 30_000 : false,
  });
}

export function useCreateAgentTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      prompt: string;
      everyMinutes: number;
      coordinatorAgentId: string;
      specialistAgentIds: string[];
    }) => api.createAgentTeam(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-teams'] }),
  });
}

export function usePatchAgentTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<AgentTeam> }) =>
      api.patchAgentTeam(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-teams'] }),
  });
}

export function useDeleteAgentTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteAgentTeam(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-teams'] }),
  });
}

export function useRunAgentTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.runAgentTeam(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent-teams'] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useAgentActionApprovals(enabled = true) {
  return useQuery({
    queryKey: ['agent-action-approvals'],
    queryFn: () => api.listAgentActionApprovals(),
    enabled,
    staleTime: 5_000,
    refetchInterval: enabled ? 15_000 : false,
  });
}

export function useApproveAgentActionApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.approveAgentActionApproval(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-action-approvals'] }),
  });
}

export function useRejectAgentActionApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.rejectAgentActionApproval(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-action-approvals'] }),
  });
}

export function useResolveAgentActionApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, outcome }: { id: string; outcome: 'executed' | 'not_executed' }) =>
      api.resolveAgentActionApproval(id, outcome),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-action-approvals'] }),
  });
}
